import { Form, IconToggle } from 'components';
import PropTypes from 'prop-types';
import React from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import { WEEKDAYS } from './openingHours';

const OpeningHoursEditor = ({ openingHours, setFieldValue, setFieldTouched }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations.openingHours' });

  const handleAvailabilityChange = (index, isOpen) => {
    setFieldValue(`opening_hours[${index}].is_closed`, !isOpen);
    setFieldTouched(`opening_hours[${index}].is_closed`, true, false);

    if (!isOpen) {
      setFieldValue(`opening_hours[${index}].opens_at`, '');
      setFieldValue(`opening_hours[${index}].closes_at`, '');
    }
  };

  return (
    <Rows>
      {openingHours.map((hours, index) => {
        const isOpen = !hours.is_closed;

        return (
          <Row key={hours.weekday}>
            <Day>{t(`weekdays.${WEEKDAYS[hours.weekday]}`)}</Day>
            <Availability>
              <IconToggle
                checked={isOpen}
                onChange={(event) => handleAvailabilityChange(index, event.target.checked)}
                leftIcon={<FaTimes />}
                rightIcon={<FaCheck />}
              />
              <Status $isOpen={isOpen}>{isOpen ? t('open') : t('closed')}</Status>
            </Availability>
            <Times>
              <TimeField>
                <TimeLabel>{t('opensAt')}</TimeLabel>
                <Form.Input
                  type="time"
                  step={60}
                  name={`opening_hours[${index}].opens_at`}
                  disabled={!isOpen}
                />
              </TimeField>
              <Separator aria-hidden="true">–</Separator>
              <TimeField>
                <TimeLabel>{t('closesAt')}</TimeLabel>
                <Form.Input
                  type="time"
                  step={60}
                  name={`opening_hours[${index}].closes_at`}
                  disabled={!isOpen}
                />
              </TimeField>
            </Times>
          </Row>
        );
      })}
    </Rows>
  );
};

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  ${media.sm`
    grid-template-columns: minmax(100px, 1fr) minmax(110px, auto) minmax(250px, 1.5fr);
    align-items: center;
  `}
`;

const Day = styled.div`
  align-self: center;
  font-weight: 600;
`;

const Availability = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Status = styled.span`
  min-width: 48px;
  color: ${({ $isOpen }) => ($isOpen ? '#212529' : '#6c757d')};
  font-size: 0.875rem;
`;

const Times = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;

  ${media.sm`
    grid-column: auto;
  `}
`;

const TimeField = styled.div`
  min-width: 0;
`;

const TimeLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  color: #6c757d;
  font-size: 0.75rem;

  ${media.sm`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `}
`;

const Separator = styled.span`
  margin-top: 20px;
  color: #6c757d;

  ${media.sm`
    margin-top: 0;
  `}
`;

OpeningHoursEditor.propTypes = {
  openingHours: PropTypes.arrayOf(
    PropTypes.shape({
      weekday: PropTypes.number.isRequired,
      opens_at: PropTypes.string,
      closes_at: PropTypes.string,
      is_closed: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
};

export default OpeningHoursEditor;
